import { useCreate, useDelete, useList, useUpdate } from '@refinedev/core'
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useMemo, useState } from 'react'

interface OrgMemoriesPanelProps {
  orgUuid: string
}

const CATEGORY_OPTIONS = ['tone', 'terminology', 'fact', 'preference', 'correction']

const OrgMemoriesPanel: React.FC<OrgMemoriesPanelProps> = ({ orgUuid }) => {
  const [scope, setScope] = useState<'org' | 'need'>('org')
  const [needId, setNeedId] = useState('')
  const [category, setCategory] = useState('correction')
  const [content, setContent] = useState('')

  const { mutate: createMemory, isLoading: isCreating } = useCreate()
  const { mutate: updateMemory } = useUpdate()
  const { mutate: archiveMemory } = useDelete()

  const { data, refetch, isLoading } = useList({
    resource: 'memories',
    filters: [{ field: 'org_uuid', operator: 'eq', value: orgUuid }],
    pagination: {
      current: 1,
      pageSize: 100,
    },
    sorters: [{ field: 'created_at', order: 'desc' }],
    queryOptions: {
      enabled: Boolean(orgUuid),
    },
  })

  const grouped = useMemo(() => {
    const byNeed: Record<string, any[]> = { 'org-wide': [] }
    for (const row of data?.data || []) {
      const key = row.need_id ? `need-${row.need_id}` : 'org-wide'
      if (!byNeed[key]) byNeed[key] = []
      byNeed[key].push(row)
    }
    return byNeed
  }, [data?.data])

  const create = () => {
    if (!content.trim()) return
    createMemory(
      {
        resource: 'memories',
        values: {
          org_uuid: orgUuid,
          scope,
          need_id: scope === 'need' && needId ? Number(needId) : null,
          category,
          content,
          source_type: 'manual',
          status: 'active',
          confidence: 0.5,
        },
      },
      {
        onSuccess: () => {
          setContent('')
          setNeedId('')
          refetch()
        },
      }
    )
  }

  const quickEdit = (row: any) => {
    const nextContent = window.prompt('Update memory content', row.content || '')
    if (nextContent === null) return

    updateMemory(
      {
        resource: 'memories',
        id: row.id,
        values: { content: nextContent },
      },
      {
        onSuccess: () => refetch(),
      }
    )
  }

  const quickArchive = (row: any) => {
    archiveMemory(
      { resource: 'memories', id: row.id },
      {
        onSuccess: () => refetch(),
      }
    )
  }

  return (
    <Stack spacing={2}>
      <Typography variant="h6">Memories</Typography>
      <Box sx={{ border: '1px solid #ddd', borderRadius: 1, p: 2 }}>
        <Typography variant="subtitle1" gutterBottom>
          Create Memory
        </Typography>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <TextField
            select
            label="Scope"
            value={scope}
            onChange={(event) => setScope(event.target.value as 'org' | 'need')}
            sx={{ minWidth: 120 }}
          >
            <MenuItem value="org">org</MenuItem>
            <MenuItem value="need">need</MenuItem>
          </TextField>
          {scope === 'need' ? (
            <TextField
              label="Need ID"
              value={needId}
              onChange={(event) => setNeedId(event.target.value)}
              sx={{ minWidth: 120 }}
            />
          ) : null}
          <TextField
            select
            label="Category"
            value={category}
            onChange={(event) => setCategory(event.target.value)}
            sx={{ minWidth: 180 }}
          >
            {CATEGORY_OPTIONS.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
        <TextField
          sx={{ mt: 2 }}
          fullWidth
          multiline
          minRows={2}
          label="Memory content"
          value={content}
          onChange={(event) => setContent(event.target.value)}
        />
        <Button sx={{ mt: 2 }} variant="contained" onClick={create} disabled={isCreating}>
          Create Memory
        </Button>
      </Box>

      {isLoading ? <Typography>Loading memories...</Typography> : null}

      {Object.entries(grouped).map(([groupName, items]) => (
        <Box key={groupName} sx={{ border: '1px solid #eee', borderRadius: 1, p: 2 }}>
          <Typography variant="subtitle1" fontWeight={700}>
            {groupName}
          </Typography>
          <Divider sx={{ my: 1 }} />
          <Stack spacing={1}>
            {items.map((row: any) => (
              <Box
                key={row.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                  border: '1px solid #f0f0f0',
                  borderRadius: 1,
                  p: 1,
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Stack direction="row" spacing={1} sx={{ mb: 0.5 }}>
                    <Chip size="small" label={row.category} />
                    <Chip size="small" variant="outlined" label={row.status} />
                  </Stack>
                  <Typography variant="body2" noWrap sx={{ maxWidth: 700 }}>
                    {row.content}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                  <Button size="small" variant="outlined" onClick={() => quickEdit(row)}>
                    Edit
                  </Button>
                  <Button
                    size="small"
                    color="warning"
                    variant="outlined"
                    onClick={() => quickArchive(row)}
                    disabled={row.status === 'archived'}
                  >
                    Archive
                  </Button>
                </Stack>
              </Box>
            ))}
          </Stack>
        </Box>
      ))}

      {!Object.keys(grouped).length ? <Alert severity="info">No memories yet.</Alert> : null}
    </Stack>
  )
}

export default OrgMemoriesPanel
